import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useOnboardingWalkthrough } from '../../../../context/OnboardingWalkthroughContext';
import ModalBackdrop from './ModalBackdrop';
import StepCard from './StepCard';
import NavigationControls from './NavigationControls';
import { highlightTab, removeAllHighlights, isMobileView } from '../../../../utils/walkthroughTabHighlighting';
import { calculateModalPosition, getArrowDirection, applyModalPosition, handleViewportResize } from '../../../../utils/walkthroughModalPositioning';
import '../../../../styles/walkthrough.css';

/**
 * WalkthroughModal Component
 * Main orchestrator that coordinates all walkthrough sub-components
 */
const WalkthroughModal = () => {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    handleNext,
    handlePrevious,
    handleSkip
  } = useOnboardingWalkthrough();

  const modalRef = useRef(null);
  const [arrowDirection, setArrowDirection] = useState('');
  const [highlightedTab, setHighlightedTab] = useState(null);

  // Handle tab highlighting and modal positioning when step changes
  useEffect(() => {
    if (!isActive || !currentStep) {
      // Clean up highlights when walkthrough is not active
      removeAllHighlights();
      return;
    }

    // Remove previous highlights
    removeAllHighlights();

    // Check if current step has a tab to highlight
    if (currentStep.highlightTab && currentStep.placement === 'adjacent') {
      const isMobile = isMobileView();
      const tabElement = highlightTab(currentStep.highlightTab, isMobile);
      setHighlightedTab(tabElement);

      // Calculate and apply modal positioning
      if (tabElement && modalRef.current) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          const positionData = calculateModalPosition(tabElement, isMobile);
          const arrow = getArrowDirection(positionData.position);
          setArrowDirection(arrow);
          applyModalPosition(modalRef.current, positionData);
        }, 50);
      }
    } else if (currentStep.highlightElement && currentStep.placement === 'adjacent') {
      // Handle custom element highlighting with retry logic
      const isMobile = isMobileView();
      
      const tryHighlight = (attempts = 0) => {
        const element = document.querySelector(currentStep.highlightElement);
        
        if (element) {
          element.classList.add('walkthrough-highlight');
          setHighlightedTab(element);

          // Calculate and apply modal positioning
          if (modalRef.current) {
            setTimeout(() => {
              const positionData = calculateModalPosition(element, isMobile);
              const arrow = getArrowDirection(positionData.position);
              setArrowDirection(arrow);
              applyModalPosition(modalRef.current, positionData);
            }, 50);
          }
        } else if (attempts < 10) {
          // Retry if element not found yet (page still loading)
          setTimeout(() => tryHighlight(attempts + 1), 100);
        }
      };
      
      tryHighlight();
    } else {
      // Center positioning for steps without tab highlighting
      setArrowDirection('');
      setHighlightedTab(null);
      if (modalRef.current) {
        applyModalPosition(modalRef.current, { position: 'center', styles: {} });
      }
    }

    // Cleanup on unmount or step change
    return () => {
      removeAllHighlights();
    };
  }, [isActive, currentStep, currentStepIndex]);

  // Handle viewport resize
  useEffect(() => {
    if (!isActive || !currentStep || (!currentStep.highlightTab && !currentStep.highlightElement)) return;

    const handleResize = () => {
      if (modalRef.current) {
        if (currentStep.highlightTab) {
          handleViewportResize(modalRef.current, currentStep.highlightTab, (positionData) => {
            const arrow = getArrowDirection(positionData.position);
            setArrowDirection(arrow);
          });
        } else if (currentStep.highlightElement) {
          const element = document.querySelector(currentStep.highlightElement);
          if (element) {
            const isMobile = isMobileView();
            const positionData = calculateModalPosition(element, isMobile);
            const arrow = getArrowDirection(positionData.position);
            setArrowDirection(arrow);
            applyModalPosition(modalRef.current, positionData);
          }
        }
      }
    };

    // Debounce resize handler
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [isActive, currentStep]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isActive) return;

    switch (e.key) {
      case 'Escape':
        handleSkip();
        break;
      case 'ArrowRight':
        if (currentStepIndex < totalSteps - 1) {
          handleNext();
        }
        break;
      case 'ArrowLeft':
        if (currentStepIndex > 0) {
          handlePrevious();
        }
        break;
      default:
        break;
    }
  }, [isActive, currentStepIndex, totalSteps, handleNext, handlePrevious, handleSkip]);

  // Add keyboard event listener
  useEffect(() => {
    if (isActive) {
      document.addEventListener('keydown', handleKeyDown);
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isActive, handleKeyDown]);

  // Focus management
  useEffect(() => {
    if (isActive) {
      // Focus the modal when it opens
      const modal = document.querySelector('[role="dialog"]');
      if (modal) {
        modal.focus();
      }
    }
  }, [isActive, currentStepIndex]);

  if (!isActive || !currentStep) {
    return null;
  }

  return (
    <ModalBackdrop isVisible={isActive}>
      <div ref={modalRef} className={`walkthrough-modal-container ${arrowDirection}`}>
        <StepCard
          step={currentStep}
          stepIndex={currentStepIndex}
          totalSteps={totalSteps}
        />
        <NavigationControls
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSkip={handleSkip}
          stepIndex={currentStepIndex}
          totalSteps={totalSteps}
        />
      </div>
    </ModalBackdrop>
  );
};

export default WalkthroughModal;
